<script setup lang="ts">
import { computed } from 'vue'
import type { Tone } from '@/lib/tone'
import { categoryAnchor, categoryTitle, severityTone, SEVERITY_LABEL } from '@/lib/tone'
import type { Scan } from '@/lib/usePolledScan'
import CountTile from './CountTile.vue'
import ToneChip from './ToneChip.vue'

const props = defineProps<{
  summary: Scan['summary']
  errorCount: number
  renderedCount: number
  /**
   * Categories to keep a row for even before they have reported anything.
   *
   * While a scan is running the server sends `byCategory` for the categories
   * that have produced findings so far, so the table grows a row at a time and
   * shoves the whole report down under the reader's eyes. Reserving the rows the
   * scan is going to fill removes the shift and, as a side effect, tells the
   * reader what is still coming.
   *
   * The list is a floor rather than a filter: a category that reports without
   * having been reserved gets a row anyway. Dropping it would take a category
   * out of this table while the findings below are still showing it, which is
   * the one thing this table must never do. It is also why a caller can leave a
   * category out on purpose. `ScanView` withholds PORT unless the request that
   * started the scan asked for a sweep, because a scan that swept no ports would
   * otherwise carry a row that never fills.
   *
   * Omit it for a finished scan, where the table should show what actually ran
   * and nothing else.
   */
  reserveCategories?: readonly string[]
}>()

const emit = defineEmits<{
  (e: 'jump', anchor: string): void
}>()

// `summary` is the only trusted source for these numbers. The findings list
// this page also renders can be capped or filtered by the server, and
// counting it instead would make the summary and the list disagree the
// moment either one changes on its own.
const failedTone = computed<Tone>(() =>
  props.summary.worstFailingSeverity ? severityTone(props.summary.worstFailingSeverity) : 'neutral',
)

interface CategoryRow {
  readonly category: string
  readonly reported: boolean
  readonly failed: number
  readonly unknown: number
  readonly passed: number
  readonly total: number
}

const categoryRows = computed<CategoryRow[]>(() => {
  // Keyed on a plain string, not the enum. An unrecognized category still has
  // to line up with whatever the server sent rather than being dropped.
  const reported = new Map<string, (typeof props.summary.byCategory)[number]>(
    props.summary.byCategory.map((entry) => [entry.category as string, entry]),
  )
  const reserved = props.reserveCategories
  const order: readonly string[] = reserved
    ? [...reserved, ...[...reported.keys()].filter((category) => !reserved.includes(category))]
    : [...reported.keys()]

  return order.map((category) => {
    const entry = reported.get(category)
    if (!entry) {
      return { category, reported: false, failed: 0, unknown: 0, passed: 0, total: 0 }
    }
    return {
      category,
      reported: true,
      failed: entry.failed,
      unknown: entry.unknown,
      passed: entry.passed,
      total: entry.total,
    }
  })
})

/**
 * Whether this table has anything at all to say about ports.
 *
 * The schema is explicit that a missing PORT category "says nothing about the
 * target: read it as 'not asked', never as 'nothing open'", and asks a client
 * that offers the sweep to say which it is. A table with no row for listening
 * services is where that misreading happens, so the note goes here, under the
 * table, rather than further down the page.
 *
 * One row is all this has to check. A row exists once the category reports, and
 * `ScanView` reserves one from the first frame when the request that started the
 * scan asked for a sweep, so a report that is going to have ports never shows the
 * note, and a report whose sweep was cut short shows it while the reason sits in
 * the checks that could not run.
 */
const noPortSweep = computed(() => !categoryRows.value.some((row) => row.category === 'PORT'))
</script>

<template>
  <section class="flex flex-col gap-6">
    <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <CountTile
        :tone="failedTone"
        glyph="alert"
        label="Failed"
        :value="props.summary.failed"
      />
      <CountTile
        tone="unknown"
        glyph="question"
        label="Could not tell"
        :value="props.summary.unknown"
      />
      <CountTile
        tone="untested"
        glyph="skip"
        label="Did not run"
        :value="props.errorCount"
      />
      <CountTile
        tone="pass"
        glyph="check"
        label="Passed"
        :value="props.summary.passed"
        muted
      />
    </div>

    <div
      v-if="props.summary.failuresBySeverity.length > 0"
      class="flex flex-col gap-2"
    >
      <p class="eyebrow">
        Failures by severity
      </p>
      <div class="flex flex-wrap gap-2">
        <ToneChip
          v-for="entry in props.summary.failuresBySeverity"
          :key="entry.severity"
          :tone="severityTone(entry.severity)"
          glyph="alert"
          :label="SEVERITY_LABEL[entry.severity]"
          :count="entry.count"
        />
      </div>
    </div>

    <!-- The wrapper scrolls, and the table is given a floor width so it scrolls
         rather than crushing five columns into a phone. Below that width the
         headers wrap into each other and "Could not tell" collides with
         "Passed", which is the one place in this report where two numbers must
         not be mistaken for each other. -->
    <div class="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
      <table class="w-full min-w-[34rem] text-left text-sm">
        <caption class="sr-only">
          Findings by category
        </caption>
        <thead>
          <tr>
            <th
              scope="col"
              class="eyebrow"
            >
              Category
            </th>
            <th
              scope="col"
              class="eyebrow text-right"
            >
              Failed
            </th>
            <th
              scope="col"
              class="eyebrow text-right"
            >
              Could not tell
            </th>
            <th
              scope="col"
              class="eyebrow text-right"
            >
              Passed
            </th>
            <th
              scope="col"
              class="eyebrow text-right"
            >
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="entry in categoryRows"
            :key="entry.category"
          >
            <td>
              <button
                type="button"
                class="tap text-accent text-left"
                @click="emit('jump', categoryAnchor(entry.category))"
              >
                {{ categoryTitle(entry.category) }}
              </button>
            </td>
            <td
              class="tabular text-right"
              :class="entry.failed === 0 || !entry.reported ? 'text-ink-faint' : undefined"
            >
              <template v-if="entry.reported">
                {{ entry.failed }}
              </template>
              <template v-else>
                <span aria-hidden="true">&middot;</span>
                <span class="sr-only">not reported yet</span>
              </template>
            </td>
            <td
              class="tabular text-right"
              :class="entry.unknown === 0 || !entry.reported ? 'text-ink-faint' : undefined"
            >
              <template v-if="entry.reported">
                {{ entry.unknown }}
              </template>
              <template v-else>
                <span aria-hidden="true">&middot;</span>
                <span class="sr-only">not reported yet</span>
              </template>
            </td>
            <td
              class="tabular text-right"
              :class="entry.passed === 0 || !entry.reported ? 'text-ink-faint' : undefined"
            >
              <template v-if="entry.reported">
                {{ entry.passed }}
              </template>
              <template v-else>
                <span aria-hidden="true">&middot;</span>
                <span class="sr-only">not reported yet</span>
              </template>
            </td>
            <td
              class="tabular text-right"
              :class="entry.total === 0 || !entry.reported ? 'text-ink-faint' : undefined"
            >
              <template v-if="entry.reported">
                {{ entry.total }}
              </template>
              <template v-else>
                <span aria-hidden="true">&middot;</span>
                <span class="sr-only">not reported yet</span>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p
      v-if="noPortSweep"
      class="text-sm text-ink-muted"
      data-testid="no-port-sweep"
    >
      No port sweep in this report. A scan sweeps ports only when it is asked to, so read this as
      ports not asked about rather than as nothing listening.
    </p>

    <p
      v-if="props.renderedCount < props.summary.total"
      class="eyebrow"
    >
      Showing {{ props.renderedCount }} of {{ props.summary.total }} findings.
    </p>
  </section>
</template>
